/**
 * Embed-tag configuration parsing and the synchronous TCF stub.
 *
 * Kept separate from index.ts (which boots on import) so these can be
 * imported side-effect-free by unit tests.
 */

export interface ScriptConfig {
  domainId: string;
  lang?: string;
  apiUrl?: string;
  anonKey?: string;
  /** Google Consent Mode defaults and updates (data-gcm="1", "basic" or "advanced"). */
  gcm?: boolean;
  /**
   * Consent Mode "advanced": the Google Tag Manager container may load before
   * consent and rely on the denied signals. Only data-gcm="advanced" sets it.
   * Plain data-gcm="1" is basic mode, where the container waits for consent
   * like any other tracker.
   */
  gcmAdvanced?: boolean;
  tcf?: boolean;
}

/**
 * Install a synchronous TCF __tcfapi stub that queues vendor commands until the
 * full CMP loads (opt-in via data-tcf, since the banner config that decides TCF
 * is fetched asynchronously). The real TcfApi drains this queue on install.
 */
export function installTcfStub(): void {
  const w = window as unknown as { __tcfapi?: unknown };
  if (w.__tcfapi) return;
  const queue: unknown[][] = [];
  const stub = function (command: string, _version: number, callback?: (d: unknown, s: boolean) => void) {
    if (command === "ping" && typeof callback === "function") {
      callback({ gdprApplies: true, cmpLoaded: false, cmpStatus: "stub", apiVersion: "2.2" }, true);
    } else {
      // eslint-disable-next-line prefer-rest-params
      queue.push(Array.prototype.slice.call(arguments));
    }
  } as unknown as { (): void; a: unknown[][] };
  stub.a = queue;
  w.__tcfapi = stub;

  if (!document.getElementById("__tcfapiLocator")) {
    const addFrame = () => {
      if (document.getElementById("__tcfapiLocator") || !document.body) return;
      const frame = document.createElement("iframe");
      frame.style.cssText = "display:none;position:absolute;width:0;height:0;";
      frame.name = "__tcfapiLocator";
      frame.id = "__tcfapiLocator";
      frame.setAttribute("aria-hidden", "true");
      frame.setAttribute("tabindex", "-1");
      document.body.appendChild(frame);
    };
    if (document.body) addFrame();
    else document.addEventListener("DOMContentLoaded", addFrame);
  }
}

export function getScriptConfig(): ScriptConfig | null {
  // Prefer the currently executing script so multiple embeds on the page
  // cannot accidentally bind to the wrong data-domain-id.
  const current =
    document.currentScript instanceof HTMLScriptElement
      ? document.currentScript
      : null;
  const script =
    current?.dataset["domainId"]
      ? current
      : document.querySelector<HTMLScriptElement>('script[src*="pritect-beacon"][data-domain-id]');
  if (!script) {
    console.warn("[Pritect Beacon] Missing data-domain-id attribute on script tag.");
    return null;
  }

  return {
    domainId: script.dataset["domainId"]!,
    lang: script.dataset["lang"],
    apiUrl: script.dataset["apiUrl"],
    anonKey: script.dataset["anonKey"],
    gcm: ["1", "true", "basic", "advanced"].includes(script.dataset["gcm"] ?? ""),
    gcmAdvanced: script.dataset["gcm"] === "advanced",
    tcf: script.dataset["tcf"] === "1" || script.dataset["tcf"] === "true",
  };
}
